import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { Container } from '../ui/Container';

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const SectionWrapper = styled.section`
  padding: var(--space-20) 0;
  position: relative;
  overflow: hidden;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    var(--color-brand-primary),
    var(--color-brand-secondary),
    var(--color-brand-primary)
  );
  background-size: 200% 200%;
  animation: ${gradientShift} 8s ease infinite;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 30% 70%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    );
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
`;

const Title = styled.h2`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: var(--font-bold);
  margin-bottom: var(--space-4);
  color: white;
`;

const Subtitle = styled.p`
  font-size: var(--text-lg);
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto var(--space-8);
  color: rgba(255, 255, 255, 0.9);
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  justify-content: center;
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4) var(--space-8);
  font-size: var(--text-lg);
  font-family: var(--font-sans);
  font-weight: var(--font-semibold);
  text-decoration: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-base);
`;

const WhiteButton = styled(Link)`
  ${buttonBase}
  background: white;
  color: var(--color-brand-primary);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  border: none;

  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    color: var(--color-brand-primary);
  }
`;

const OutlineButton = styled(Link)`
  ${buttonBase}
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.5);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
    color: white;
  }
`;

interface CTASectionProps {
  title?: string;
  subtitle?: string;
  primaryCTA?: {
    label: string;
    to: string;
  };
  secondaryCTA?: {
    label: string;
    to: string;
  };
}

export function CTASection({
  title = "Ready to Build Something Great?",
  subtitle = "Whether you want to try our apps or hire our team for a custom project, we'd love to hear from you.",
  primaryCTA = { label: "Explore Our Apps", to: "/products" },
  secondaryCTA = { label: "Start a Project", to: "/contact" },
}: CTASectionProps) {
  return (
    <SectionWrapper>
      <Background />
      <Container>
        <Content>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
          <CTAGroup>
            <WhiteButton to={primaryCTA.to}>
              {primaryCTA.label}
            </WhiteButton>
            <OutlineButton to={secondaryCTA.to}>
              {secondaryCTA.label}
            </OutlineButton>
          </CTAGroup>
        </Content>
      </Container>
    </SectionWrapper>
  );
}
